<template>
  <div v-if="pathways" class="select-pathway">
    <div class="row">
      <div class="col-md-12 text-center">
        <h2 class="choose-pathway" data-test="pp-choose-pathway-title">Choose a pathway</h2>
      </div>
    </div>
    <div class="row pathway-tiles" v-if="!isExpandedView" data-test="pp-pathway-grid">
      <div
        class="pathway-tile"
        v-for="(pathway, index) in pathways"
        :key="index"
        data-test="pp-pathway-tiles"
      >
        <PathwayTile
          :data-test="`pp-${pathway.pathwayUid}`"
          :pathway="pathway"
          :isExpandedView="isExpandedView"
          @toggle-expanded-view="toggleExpandedView"
        />
      </div>
    </div>
    <div class="row pathway-carousel expanded-tiles" v-else>
      <Carousel
        icon-control-left="btn btn-default-fab-chevron left"
        icon-control-right="btn btn-default-fab-chevron right"
        :interval="0"
        :indicators="false"
        :controls="controls"
      >
        <Slide v-for="(pathway, index) in pathways" :key="index">
          <PathwayTile
            :data-test="`pp-${pathway.pathwayUid}-expanded`"
            :pathway="pathway"
            :isExpandedView="isExpandedView"
            @toggle-expanded-view="toggleExpandedView"
          />
        </Slide>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Carousel, Slide } from "uiv";
import { CmsPathwaysRelationship } from "../../services/cms/cms-type-definitions";
import { PathwayTileData } from "../team-outcomes/select-team-pathway.vue";
import PathwayTile from "../team-outcomes/pathway-tile.vue";
import { Analytics } from "../../services/analytics/analytics";
import cms from "../../services/cms/cms";

const props = defineProps<{ relatedPathways: CmsPathwaysRelationship[] }>();

const emit = defineEmits(["display-next-button"]);

const activePathway = ref<PathwayTileData | null>(null);

const carouselPathways = ref<PathwayTileData[] | null>(null);

const isExpandedView = ref(false);

const controls = ref(true);

const mapToData = (p): PathwayTileData => {
  return {
    pathwayUid: p.pathway.uid,
    pathwayId: p.pathway.id,
    data: p.pathway.data,
  };
};

const handleDisplayControls = (pathwaysArr: PathwayTileData[]): void => {
  controls.value = pathwaysArr.length > 1;
};

const preparePathways = (): PathwayTileData[] => {
  return props.relatedPathways.filter((p) => p.uid !== "").map((p) => mapToData(p));
};

const pathways = computed<PathwayTileData[]>(() => {
  if (isExpandedView.value && carouselPathways.value) {
    handleDisplayControls(carouselPathways.value);
    return carouselPathways.value;
  }
  return preparePathways();
});

const currentIndex = computed<number>(() => {
  const pathwayTitle = activePathway?.value?.data.title;
  const pathwaysList = preparePathways();
  return pathwaysList.findIndex((pathway) => {
    return pathway.data.title === pathwayTitle;
  });
});

const getCarouselOrder = (): void => {
  const pathwayIndex = currentIndex;
  const pathwaysArr = preparePathways();
  const firstSection = pathwaysArr.slice(pathwayIndex.value);
  const secondSection = pathwaysArr.slice(0, pathwayIndex.value);
  carouselPathways.value = firstSection.concat(secondSection);
};

const toggleExpandedView = ({
  pathway,
  isOpen,
}: {
  pathway: PathwayTileData;
  isOpen: boolean;
}): void => {
  activePathway.value = isExpandedView.value ? null : pathway;
  getCarouselOrder();
  isExpandedView.value = isOpen;
  if (isExpandedView.value) {
    Analytics.action("Clicked Personal Pathway", "PersonalPathwayActions", {
      outcomeTitle: cms.asText(activePathway?.value?.data?.goal_relationship?.data?.full_title),
      outcomeId: activePathway?.value?.data?.goal_relationship?.id,
      outcomeUid: activePathway?.value?.data?.goal_relationship?.uid,
      pathwayTitle: cms.asText(activePathway?.value?.data.title),
      pathwayUid: activePathway?.value?.pathwayUid,
    });
  }
  emit("display-next-button", activePathway?.value?.pathwayUid);
};
</script>

<style scoped lang="less">
@import "../../styles/outcomes/select-pathway";
</style>
<style lang="less">
@import "../../styles/outcomes/prismic-select-pathway";
</style>
